import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from 'store/rootStore';
import { formUtils } from '@insurancemenu/imenu-components-lib';
import { FormObjectFromJSON, ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import {
  CartBrowsingCriteria,
  Portfolio,
  BrowsingInfo,
} from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { INITIAL_BROWSING_CRITERIA } from 'store/domain/cartStoreV2';
import { requestPortfolioConfigForm, INITIAL_FORM_OBJECT } from 'api/operations/formService';

export default class BrowsingPortfolioConfigModalStore {
  rootStore: RootStore;
  form: FormObjectFromJSON;
  dynamicForm: null | formUtils.DynamicFormController;

  loading = false;
  showConfigurePlanModal = false;
  productLineId: ProductLineId = 'GroupDental';
  portfolioBrowsingCriteria: CartBrowsingCriteria;

  setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  async openConfigurePlanModal(portfolio: Portfolio, effectiveDate: string, bcIndex: number | null): Promise<void> {
    this.showConfigurePlanModal = true;
    this.setLoading(true);
    this.productLineId = portfolio.productLineId;
    this.dynamicForm = null;
    try {
      const res = await requestPortfolioConfigForm(portfolio.id, effectiveDate);
      runInAction(() => {
        this.form = res.data.getBrowsingConfigFormByPortfolioId[0];
        // Bc index from default bc card is needed to set correct product line BC
        const bc = portfolio.bcVersions[bcIndex || 0];

        if (bc && bc[this.productLineId]) {
          this.portfolioBrowsingCriteria = bc;
          this.createFormController(bc[this.productLineId]);
        }
      });
    } catch (error) {
      this.rootStore.comparisonStore.showToast(
        'An error has occurred while attempting to retrieve the form for portfolio configuration.',
        ToastColorTypes.WARNING,
      );
      this.closeConfigurePlanModal();
    } finally {
      this.setLoading(false);
    }
  }

  closeConfigurePlanModal(): void {
    this.showConfigurePlanModal = false;
  }

  createFormController(answers: BrowsingInfo | undefined): void {
    this.setLoading(true);
    this.dynamicForm = new formUtils.DynamicFormController(this.form, this.rootStore.userStore.userRoles);
    if (answers) {
      this.dynamicForm.populateFields(answers);
    }
    this.setLoading(false);
  }

  saveAnswers(): void {
    const browsingCriteriaAnswersFromForm = this.portfolioBrowsingCriteria[this.productLineId] || {};

    Object.entries(this.dynamicForm!.getCanonicalAnswers()).forEach(([key, value]) => {
      browsingCriteriaAnswersFromForm[key] = value?.toString() || '';
    });

    this.portfolioBrowsingCriteria[this.productLineId] = browsingCriteriaAnswersFromForm;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.dynamicForm = null;
    this.form = INITIAL_FORM_OBJECT;
    this.portfolioBrowsingCriteria = INITIAL_BROWSING_CRITERIA;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
